import Icon from "components/ui/Icon";
import Loading from "components/ui/Loading";
import { useState } from "react";
import {
  getDashboard,
  getOnboarding,
  getUsersProfile,
} from "store/api/apiSlice";
import {
  ApiUsersCurrentTransactionSource,
  ApiUsersProduct,
} from "utils/api/users";
import AccountBalance from "./AccountBalance";
import Card from "./Card";
import EwaEnabledInfo from "./EwaEnabledInfo";
import OnboardingCta from "./OnboardingCta";
import PaydayCta from "./PaydayCta";
import * as Styled from "./styled";
import Transactions from "./Transactions";

const getEarningsBalanceStatus = ({
  isEwaActive,
  isEwaFlex,
}: {
  isEwaActive: boolean;
  isEwaFlex: boolean;
}) => {
  if (!isEwaFlex) return undefined;

  return isEwaActive ? "primary" : "inactive";
};

const getCheckingBalanceStatus = ({
  isOnboardingComplete,
  isEwaActive,
  isEwaFlex,
}: {
  isOnboardingComplete: boolean;
  isEwaActive: boolean;
  isEwaFlex: boolean;
}) => {
  if (!isEwaFlex || !isOnboardingComplete) return undefined;

  return isEwaActive ? "secondary" : "active";
};

const Dashboard = () => {
  const [isOnboarded, setIsOnboarded] = useState(false);
  const onboarding = getOnboarding();
  const { data: dashboard, ...dashboardQuery } = getDashboard({
    pollingInterval: 2_000,
  });
  const { data: userProfile } = getUsersProfile();

  const onOnboardingSuccess = () => {
    if (!dashboardQuery.isUninitialized) {
      dashboardQuery.refetch();
    }
    if (!onboarding.isUninitialized) {
      onboarding.refetch();
    }

    setIsOnboarded(true);
  };

  if (dashboardQuery.isLoading) return <Loading />;

  const isOnboardingComplete = onboarding.data?.complete;
  const isEwaFlex = userProfile?.product === ApiUsersProduct.ewaFlex;
  const showFlexUI = isOnboardingComplete && isEwaFlex;
  const isEwaActive =
    isEwaFlex &&
    [
      ApiUsersCurrentTransactionSource.ewa,
      ApiUsersCurrentTransactionSource.flex,
    ].includes(userProfile?.currentTransactionSource);
  const earningsBalanceStatus = getEarningsBalanceStatus({
    isEwaActive,
    isEwaFlex,
  });
  const checkingBalanceStatus = getCheckingBalanceStatus({
    isEwaActive,
    isEwaFlex,
    isOnboardingComplete,
  });

  return (
    <Styled.Wrapper key={String(isOnboarded)}>
      <Styled.Header>
        <Styled.CardWrapper>
          <Styled.Card>
            <Card />
          </Styled.Card>
        </Styled.CardWrapper>

        <Styled.HeaderTile>
          {showFlexUI && (
            <Styled.HeaderSettingsLinkWrapper>
              <Styled.HeaderSettingsLink
                to="/transaction-settings"
                viewTransition
              >
                <Icon name="gearDuo" />
              </Styled.HeaderSettingsLink>
            </Styled.HeaderSettingsLinkWrapper>
          )}

          <Styled.HeaderInfo>
            <Styled.HeaderSpending>
              {isOnboardingComplete && (
                <Styled.HeaderSpendingItem>
                  <AccountBalance
                    label="Earnings Balance"
                    amount={dashboard?.earningsBalance}
                    status={earningsBalanceStatus}
                  />
                </Styled.HeaderSpendingItem>
              )}

              {isEwaFlex && (
                <Styled.HeaderSpendingItem>
                  <Styled.HeaderSpendingDivider />

                  <AccountBalance
                    label="Checking Balance"
                    amount={dashboard?.checkingBalance}
                    status={checkingBalanceStatus}
                  />
                </Styled.HeaderSpendingItem>
              )}
            </Styled.HeaderSpending>

            {isOnboardingComplete && <EwaEnabledInfo />}
          </Styled.HeaderInfo>

          {!onboarding.isLoading && !isOnboardingComplete && (
            <OnboardingCta onSuccess={onOnboardingSuccess} />
          )}
        </Styled.HeaderTile>
      </Styled.Header>

      {showFlexUI && dashboard?.outstandingEwaTransactions > 0 && (
        <Styled.HeaderCtaWrapper>
          <PaydayCta />
        </Styled.HeaderCtaWrapper>
      )}

      <Transactions />
    </Styled.Wrapper>
  );
};

export default Dashboard;
